<template>
  <div class="fs-page">
    <FansStoreChrome @home="goHome" />

    <div class="fs-container">
      <div class="fs-search-section">
        <div class="fs-tabs">
          <button
            v-for="cat in categories"
            :key="cat.key"
            type="button"
            class="fs-tab"
            :class="{ 'fs-tab--active': category === cat.key }"
            @click="onCategory(cat.key)"
          >{{ cat.label }}</button>
        </div>
        <div class="fs-search-box">
          <input
            v-model="keyword"
            class="fs-search-input"
            type="text"
            placeholder="请输入查询名称"
            @keydown.enter="onSearch"
          />
          <button type="button" class="fs-search-btn" @click="onSearch">搜索</button>
        </div>
      </div>

      <div v-if="loading" class="fs-empty">加载中…</div>
      <div v-else class="fs-grid">
        <button
          v-for="item in filteredProducts"
          :key="item.id"
          type="button"
          class="fs-card"
          @click="openProduct(item)"
        >
          <div class="fs-icon" :class="`fs-icon--${iconClassFor(item.icon)}`">{{ item.icon }}</div>
          <div class="fs-card-body">
            <div class="fs-card-title">{{ item.title }}</div>
            <div class="fs-card-desc">{{ item.desc }}</div>
            <div class="fs-card-price">{{ item.priceText }}</div>
          </div>
        </button>
      </div>
      <div v-if="!loading && !filteredProducts.length" class="fs-empty">暂无匹配商品</div>
    </div>

    <footer class="fs-footer">Fans Store - 粉丝商城 © 2025</footer>
  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import FansStoreChrome from '../components/FansStoreChrome.vue';
import {
  FANS_CATEGORIES,
  fetchFansCatalog,
  iconClassFor,
  normalizeFansService,
} from '../data/fansStore';
import { buildReferralQuery, resolveReferralContext } from '../util/deeplink';

export default {
  name: 'FansStore',
  components: { FansStoreChrome },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const category = ref('全部');
    const keyword = ref('');
    const searchText = ref('');
    const loading = ref(false);
    const products = ref([]);
    const categories = ref([...FANS_CATEGORIES]);
    const referralCode = ref('');
    const gameGroupId = ref('');

    const filteredProducts = computed(() => {
      const q = searchText.value.trim().toLowerCase();
      return products.value.filter((item) => {
        const catOk = category.value === '全部' || item.category === category.value;
        const textOk = !q || item.title.toLowerCase().includes(q) || item.desc.toLowerCase().includes(q);
        return catOk && textOk;
      });
    });

    const homeQuery = () => buildReferralQuery(referralCode.value, gameGroupId.value);

    onMounted(async () => {
      const ctx = resolveReferralContext(route);
      referralCode.value = ctx.referralCode;
      gameGroupId.value = ctx.gameGroupId;
      if (route.query.category) category.value = String(route.query.category);

      loading.value = true;
      try {
        const { categories: cats, products: list } = await fetchFansCatalog();
        categories.value = cats?.length ? cats : [...FANS_CATEGORIES];
        products.value = (list || []).map(normalizeFansService);
      } catch (e) {
        console.error(e);
        products.value = [];
      } finally {
        loading.value = false;
      }
    });

    const goHome = () => {
      router.push({ path: '/fansStore', query: homeQuery() });
    };

    const onCategory = (key) => {
      category.value = key;
    };

    const onSearch = () => {
      searchText.value = keyword.value;
    };

    const openProduct = (item) => {
      router.push({
        path: '/fansStore/record',
        query: {
          ...homeQuery(),
          id: item.id,
          title: item.title,
        },
      });
    };

    return {
      categories,
      category,
      keyword,
      loading,
      filteredProducts,
      iconClassFor,
      goHome,
      onCategory,
      onSearch,
      openProduct,
    };
  },
};
</script>

<style scoped>
.fs-page {
  min-height: 100vh;
  min-height: 100dvh;
  background: #f5f5f5;
  color: #333;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
  padding-bottom: 90px;
  box-sizing: border-box;
}

.fs-container {
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 0 20px 40px;
  box-sizing: border-box;
}

.fs-search-section {
  padding: 15px 20px;
  background: #fff;
  margin: 15px 0;
}

.fs-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  margin-bottom: 15px;
}

.fs-tab {
  flex: 1 0 auto;
  min-width: 80px;
  max-width: 160px;
  text-align: center;
  padding: 8px 16px;
  background: #f0f0f0;
  border: 0;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  white-space: nowrap;
  color: #333;
}

.fs-tab--active {
  background: #1890ff;
  color: #fff;
}

.fs-search-box {
  display: flex;
}

.fs-search-input {
  flex: 1;
  min-width: 0;
  padding: 10px 15px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
  outline: none;
}

.fs-search-btn {
  background: #1890ff;
  color: #fff;
  border: 0;
  padding: 0 20px;
  margin-left: 10px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.fs-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
}

.fs-card {
  background: #fff;
  border: 0;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  text-align: left;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.fs-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  color: #fff;
  font-size: 20px;
  flex-shrink: 0;
}

.fs-icon--blue { background: linear-gradient(135deg, #5b8cff, #3655b3); }
.fs-icon--orange { background: linear-gradient(135deg, #ff7847, #ff5722); }
.fs-icon--teal { background: linear-gradient(135deg, #4cd2c0, #26a69a); }

.fs-card-body { flex: 1; min-width: 0; }

.fs-card-title {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 8px;
  color: #333;
}

.fs-card-desc {
  font-size: 12px;
  color: #666;
  line-height: 1.5;
  margin-bottom: 10px;
  height: 54px;
  overflow: hidden;
}

.fs-card-price {
  color: #ff5722;
  font-weight: 700;
  font-size: 16px;
}

.fs-empty {
  text-align: center;
  color: #999;
  padding: 48px 0;
  font-size: 14px;
}

.fs-footer {
  width: 100%;
  background: linear-gradient(90deg, #16233a 0%, #22395c 100%);
  color: #fff;
  text-align: center;
  padding: 22px 0 18px;
  font-size: 15px;
  letter-spacing: 1px;
  position: fixed;
  left: 0;
  bottom: 0;
  z-index: 100;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
}

@media (max-width: 1024px) {
  .fs-grid { grid-template-columns: repeat(2, 1fr); }
}

@media (max-width: 768px) {
  .fs-container { padding: 0 10px 40px; }
  .fs-grid { grid-template-columns: 1fr; }
  .fs-tabs {
    overflow-x: auto;
    flex-wrap: nowrap;
    justify-content: flex-start;
    -webkit-overflow-scrolling: touch;
  }
  .fs-tab { font-size: 12px; padding: 6px 12px; }
  .fs-search-section { padding: 10px; }
  .fs-card { padding: 15px; }
  .fs-icon { width: 40px; height: 40px; font-size: 16px; }
  .fs-card-title { font-size: 14px; }
  .fs-card-desc { height: auto; }
  .fs-footer { font-size: 12px; padding: 15px 0 12px; }
}
</style>
